import type {EstablishedAhpToolConfirmation, ToolConfirmationResponse} from '@github-ui/ahp-session-client/contracts'
import {ChoiceButton, ChoiceButtonGroup} from '@github-ui/agent-sessions-interface/components/ChoiceButton'
import {UserChoiceCard} from '@github-ui/agent-sessions-interface/components/UserChoiceCard'
import {announce} from '@github-ui/aria-live'
import {isShortcutAllowed} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import {Stack, Text} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {useCallback, useEffect, useState, type FocusEventHandler} from 'react'

import {useOptionalEstablishedAhpSession} from '../hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '../hooks/use-established-ahp-session-view'
import styles from './EstablishedAhpSessionConfirmation.module.css'

export interface EstablishedAhpSessionConfirmationProps {
  confirmation: EstablishedAhpToolConfirmation
  className?: string
  'data-testid'?: string
  readOnly?: boolean
  onBlurCapture?: FocusEventHandler<HTMLDivElement>
  onFocusCapture?: FocusEventHandler<HTMLDivElement>
  onConfirm?: (response: ToolConfirmationResponse) => void
}

function EstablishedAhpRiskAssessment({riskAssessment}: {riskAssessment?: string}) {
  useEffect(() => {
    if (riskAssessment) announce(riskAssessment)
  }, [riskAssessment])

  if (!riskAssessment) return null

  return (
    <InlineMessage variant="warning" size="small">
      <Text size="small" weight="medium">
        {riskAssessment}
      </Text>
    </InlineMessage>
  )
}

export function EstablishedAhpConfirmationPreview({
  detail,
  kind,
  riskAssessment,
}: Pick<EstablishedAhpToolConfirmation, 'detail' | 'kind' | 'riskAssessment'>) {
  if (!detail && !riskAssessment) return null

  const isCommandConfirmation = kind === 'commands' || kind === 'shell'
  const detailContent = detail ? (
    isCommandConfirmation ? (
      <pre className={styles.commandDetail}>
        <code>{`$ ${detail}`}</code>
      </pre>
    ) : (
      detail
    )
  ) : null

  return (
    <Stack direction="vertical" gap="condensed">
      {detailContent}
      <EstablishedAhpRiskAssessment riskAssessment={riskAssessment} />
    </Stack>
  )
}

/**
 * Shared active-AHP tool confirmation surface. The host chooses placement and
 * focus handling; command dispatch remains on the established session.
 */
export function EstablishedAhpSessionConfirmation({
  confirmation,
  className,
  'data-testid': dataTestId,
  readOnly = false,
  onBlurCapture,
  onFocusCapture,
  onConfirm,
}: EstablishedAhpSessionConfirmationProps) {
  const session = useOptionalEstablishedAhpSession()
  const view = useOptionalEstablishedAhpSessionViewState()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string>()
  const isReadOnly = readOnly || view.passive
  const respond = useCallback(
    (option: EstablishedAhpToolConfirmation['options'][number]) => {
      if (isSubmitting || isReadOnly) return
      setIsSubmitting(true)
      setError(undefined)
      const response: ToolConfirmationResponse = {
        approved: option.kind === 'approve',
        toolCallId: confirmation.toolCallId,
        turnId: confirmation.turnId,
        ...(confirmation.resultConfirmation ? {resultConfirmation: true} : {}),
        ...(option.selectedOptionId ? {selectedOptionId: option.selectedOptionId} : {}),
        ...(option.forSession ? {forSession: true} : {}),
        ...(confirmation.chat ? {chat: confirmation.chat} : {}),
      }
      try {
        if (onConfirm) {
          onConfirm(response)
        } else if (session) {
          session.commands.confirmTool(response)
        } else {
          throw new Error('An established AHP session is required to confirm this action.')
        }
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : 'Confirmation response could not be sent.')
        setIsSubmitting(false)
      }
    },
    [confirmation, isReadOnly, isSubmitting, onConfirm, session],
  )

  useEffect(() => {
    if (isSubmitting || isReadOnly) return

    const handleDigitKey = (event: KeyboardEvent) => {
      const {key, metaKey, ctrlKey, altKey} = event
      if (metaKey || ctrlKey || altKey) return
      if (!isShortcutAllowed(event)) return
      if (key < '1' || key > '9') return

      const option = confirmation.options[Number(key) - 1]
      if (!option) return

      event.preventDefault()
      respond(option)
    }

    document.addEventListener('keydown', handleDigitKey)
    return () => document.removeEventListener('keydown', handleDigitKey)
  }, [confirmation.options, isReadOnly, isSubmitting, respond])

  const previewContent =
    confirmation.detail || confirmation.riskAssessment ? (
      <EstablishedAhpConfirmationPreview
        detail={confirmation.detail}
        kind={confirmation.kind}
        riskAssessment={confirmation.riskAssessment}
      />
    ) : undefined

  return (
    <div className={className} data-testid={dataTestId} onBlurCapture={onBlurCapture} onFocusCapture={onFocusCapture}>
      <UserChoiceCard title={confirmation.title} previewContent={previewContent}>
        <Stack direction="vertical" gap="normal">
          {error ? <div role="alert">{error}</div> : null}
          {isReadOnly ? (
            <p>This session is open in read-only mode.</p>
          ) : (
            <ChoiceButtonGroup aria-label="Tool confirmation choices" autoFocus={false}>
              {confirmation.options.map((option, index) => (
                <ChoiceButton
                  key={option.id}
                  number={index + 1}
                  label={option.label}
                  ariaLabel={option.label}
                  disabled={isSubmitting}
                  onClick={() => respond(option)}
                />
              ))}
            </ChoiceButtonGroup>
          )}
        </Stack>
      </UserChoiceCard>
    </div>
  )
}

EstablishedAhpSessionConfirmation.displayName = 'EstablishedAhpSessionConfirmation'
